import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n, { setAdminLanguage } from "@/i18n";
import type { en } from "@/i18n/en";
import { CONTENT_PAGE_EDIT_ROLES, PROMO_BAR_EDIT_ROLES, type UserRole } from "@ckgo2/shared/types";
import { useAuthStore } from "@/stores/authStore";
import NotificationBell from "@/components/common/NotificationBell";
import { AgentProvider } from "@/components/agent/AgentProvider";
import MakejWidget from "@/components/agent/MakejWidget";
import {
  LayoutDashboard,
  MapPin,
  Users,
  Plane,
  ShoppingCart,
  UserCircle,
  Inbox,
  Sparkles,
  Zap,
  Workflow,
  Image,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  ScrollText,
  BookOpen,
  ChevronDown,
  Shield,
  FileText,
  BarChart3,
  Gift,
  ClipboardList,
  ListOrdered,
  Activity,
  Newspaper,
  HelpCircle,
  Briefcase,
  Mic,
  Home,
  Cloud,
  UserPlus,
  Building2,
  Package,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  LayoutGrid,
  Receipt,
  Wallet,
  FileSignature,
  MessageSquareWarning,
  Globe2,
  Megaphone,
  PartyPopper,
  Star,
  type LucideIcon,
} from "lucide-react";
import clsx from "clsx";

type NavSectionKey = `nav.sections.${keyof (typeof en)["translation"]["nav"]["sections"] & string}`;
type NavItemKey = `nav.items.${keyof (typeof en)["translation"]["nav"]["items"] & string}`;

interface NavItem {
  nameKey: NavItemKey;
  href: string;
  icon: LucideIcon;
  badge?: string;
  /**
   * Role, které položku smí vidět. Bez omezení ji vidí každý přihlášený — plošné role-aware
   * skrývání adminu je starší, širší dluh, tenhle řez gatuje jen horní lištu. Server-side
   * gate zůstává ten závazný, tohle je jen „nenabízet, co stejně skončí 403".
   */
  allowedRoles?: readonly UserRole[];
}

interface NavSection {
  titleKey: NavSectionKey;
  items: NavItem[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

// Grouped sidebar (vision §3). New items (Inquiries, Inbox) are added to their
// section as their pages land in later Block 1 slices.
const navSections: NavSection[] = [
  {
    titleKey: "nav.sections.myDay",
    items: [{ nameKey: "nav.items.dashboard", href: "/", icon: LayoutDashboard }],
  },
  {
    titleKey: "nav.sections.inbox",
    items: [{ nameKey: "nav.items.inbox", href: "/inbox", icon: Inbox }],
  },
  {
    titleKey: "nav.sections.sales",
    items: [
      { nameKey: "nav.items.inquiries", href: "/leads", icon: UserPlus },
      { nameKey: "nav.items.orders", href: "/orders", icon: ShoppingCart },
      { nameKey: "nav.items.customers", href: "/customers", icon: UserCircle },
    ],
  },
  {
    titleKey: "nav.sections.operations",
    items: [
      { nameKey: "nav.items.serviceQueue", href: "/services", icon: ClipboardList },
      { nameKey: "nav.items.flightDesk", href: "/flights", icon: Plane },
      { nameKey: "nav.items.season", href: "/season", icon: LayoutGrid },
      { nameKey: "nav.items.schedule", href: "/schedule", icon: CalendarClock },
      { nameKey: "nav.items.departures", href: "/departures", icon: CalendarRange },
      { nameKey: "nav.items.calendar", href: "/calendar", icon: CalendarDays },
      { nameKey: "nav.items.documents", href: "/documents", icon: FileSignature },
      { nameKey: "nav.items.complaints", href: "/complaints", icon: MessageSquareWarning },
      { nameKey: "nav.items.suppliers", href: "/suppliers", icon: Building2 },
      { nameKey: "nav.items.serviceCatalog", href: "/supplier-components", icon: Package },
      { nameKey: "nav.items.templates", href: "/templates", icon: FileText },
    ],
  },
  {
    titleKey: "nav.sections.finance",
    items: [
      { nameKey: "nav.items.invoices", href: "/invoices", icon: Receipt },
      { nameKey: "nav.items.payments", href: "/finance", icon: Wallet },
      { nameKey: "nav.items.vouchers", href: "/vouchers", icon: Gift },
    ],
  },
  {
    titleKey: "nav.sections.content",
    collapsible: true,
    defaultCollapsed: true,
    items: [
      { nameKey: "nav.items.destinations", href: "/destinations", icon: MapPin },
      { nameKey: "nav.items.guides", href: "/guides", icon: Users },
      { nameKey: "nav.items.destinationTeams", href: "/destination-teams", icon: Users },
      { nameKey: "nav.items.trips", href: "/trips", icon: Plane },
      { nameKey: "nav.items.tripOrder", href: "/trips/order", icon: ListOrdered },
      { nameKey: "nav.items.upgrades", href: "/premium-upgrades", icon: Sparkles },
      { nameKey: "nav.items.inspirations", href: "/inspirations", icon: Activity },
      { nameKey: "nav.items.faq", href: "/faqs", icon: HelpCircle },
      { nameKey: "nav.items.jobOffers", href: "/job-offers", icon: Briefcase },
      { nameKey: "nav.items.holidays", href: "/holidays", icon: PartyPopper },
      { nameKey: "nav.items.podcastEpisodes", href: "/podcast-episodes", icon: Mic },
      { nameKey: "nav.items.blog", href: "/blog", icon: Newspaper },
      {
        nameKey: "nav.items.contentPages",
        href: "/content-pages",
        icon: FileText,
        allowedRoles: CONTENT_PAGE_EDIT_ROLES,
      },
      { nameKey: "nav.items.reviews", href: "/reviews", icon: MessageSquare },
      { nameKey: "nav.items.googleReviews", href: "/google-reviews", icon: Star },
      { nameKey: "nav.items.quiz", href: "/quiz", icon: ClipboardList },
      { nameKey: "nav.items.homepage", href: "/homepage", icon: Home },
      {
        nameKey: "nav.items.promoBar",
        href: "/promo-bar",
        icon: Megaphone,
        allowedRoles: PROMO_BAR_EDIT_ROLES,
      },
      { nameKey: "nav.items.media", href: "/media", icon: Image },
    ],
  },
  {
    titleKey: "nav.sections.marketing",
    items: [
      { nameKey: "nav.items.newsletterStudio", href: "/newsletter-studio", icon: Sparkles },
      { nameKey: "nav.items.automations", href: "/automations", icon: Zap },
      { nameKey: "nav.items.journeys", href: "/journeys", icon: Workflow },
    ],
  },
  {
    titleKey: "nav.sections.system",
    items: [
      { nameKey: "nav.items.analytics", href: "/analytics", icon: BarChart3 },
      { nameKey: "nav.items.wiki", href: "/wiki", icon: BookOpen },
      { nameKey: "nav.items.countryKb", href: "/country-kb", icon: Globe2 },
      { nameKey: "nav.items.auditLog", href: "/audit", icon: ScrollText },
      { nameKey: "nav.items.users", href: "/users", icon: Shield },
      { nameKey: "nav.items.integrations", href: "/integrations", icon: Cloud },
    ],
  },
];

function LanguageSwitcher() {
  return (
    <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 p-0.5 dark:border-gray-600">
      {(["cs", "en"] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => lang !== i18n.language && setAdminLanguage(lang)}
          className={clsx(
            "rounded-md px-2 py-1 text-xs font-semibold uppercase transition-colors",
            i18n.language === lang
              ? "bg-primary-600 text-white"
              : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white",
          )}
        >
          {lang}
        </button>
      ))}
    </div>
  );
}

const flatNav = navSections.flatMap((s) => s.items);

/**
 * Shoda cesty s položkou menu na HRANICI SEGMENTU: `/trips` platí pro `/trips`
 * i `/trips/42`, ale ne pro `/trips-archiv`. Prosté `startsWith` tuhle hranici nemá.
 */
function matchesPath(href: string, pathname: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Aktivní položka je PRÁVĚ JEDNA — ta s nejdelší shodnou cestou. Vnořené položky
 * (`/trips` × `/trips/order`) jinak vyhoví obě: sidebar by rozsvítil dvě naráz a mobilní
 * hlavička by ukázala tu, která v poli stojí dřív. Vyhodnocuje se jednou a zbytek
 * komponenty pak porovnává identitou, takže žádné další místo nemusí pravidlo znát.
 */
function resolveActiveItem(pathname: string): NavItem | undefined {
  return flatNav
    .filter((item) => matchesPath(item.href, pathname))
    .sort((a, b) => b.href.length - a.href.length)[0];
}

/** Položky, na které role nemá právo, se v menu vůbec nenabízejí (viz `NavItem.allowedRoles`). */
function visibleItems(items: NavItem[], role: string | undefined): NavItem[] {
  return items.filter((i) => !i.allowedRoles || i.allowedRoles.includes(role as UserRole));
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();

  // Per-section collapse state, seeded from defaults. A section is force-expanded
  // when the active route lives inside it (so you never lose your place).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      navSections.filter((s) => s.collapsible).map((s) => [s.titleKey, !!s.defaultCollapsed]),
    ),
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const activeItem = resolveActiveItem(location.pathname);
  const currentTitle = activeItem ? t(activeItem.nameKey) : t("common.admin");

  return (
    <AgentProvider>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={clsx(
            "bg-sidebar fixed inset-y-0 left-0 z-50 flex w-64 transform flex-col transition-transform duration-200 ease-in-out lg:static lg:translate-x-0",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-6">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-white.svg" alt="CK Go2" className="h-8 w-auto" />
              <span className="bg-accent-500 rounded px-1.5 py-0.5 text-xs font-medium text-white">
                {t("common.admin")}
              </span>
            </Link>
            <button
              title={t("common.actions.close")}
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-white lg:hidden"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="scrollbar-hide mt-4 flex-1 space-y-4 overflow-y-auto px-3 pb-4">
            {navSections.map((section) => {
              const items = visibleItems(section.items, user?.role);
              if (items.length === 0) return null;
              const hasActive = activeItem !== undefined && items.includes(activeItem);
              const isCollapsed = section.collapsible && collapsed[section.titleKey] && !hasActive;
              return (
                <div key={section.titleKey}>
                  <button
                    type="button"
                    onClick={() =>
                      section.collapsible &&
                      setCollapsed((prev) => ({
                        ...prev,
                        [section.titleKey]: !prev[section.titleKey],
                      }))
                    }
                    className={clsx(
                      "flex w-full items-center justify-between px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400",
                      section.collapsible && "hover:text-gray-200",
                    )}
                  >
                    <span>{t(section.titleKey)}</span>
                    {section.collapsible && (
                      <ChevronDown
                        className={clsx(
                          "h-3.5 w-3.5 transition-transform",
                          isCollapsed && "-rotate-90",
                        )}
                      />
                    )}
                  </button>

                  {!isCollapsed &&
                    items.map((item) => {
                      const isActive = item === activeItem;
                      return (
                        <Link
                          key={item.nameKey}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={clsx(
                            "mb-0.5 flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors",
                            isActive
                              ? "bg-primary-600 text-white"
                              : "text-gray-300 hover:bg-white/10 hover:text-white",
                          )}
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          <span className="truncate">{t(item.nameKey)}</span>
                          {item.badge && (
                            <span className="bg-accent-500 ml-auto rounded px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                </div>
              );
            })}
          </nav>

          {/* Bottom section */}
          <div className="mt-auto border-t border-gray-700 p-4">
            <Link
              to="/settings"
              className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Settings className="h-5 w-5" />
              {t("common.settings")}
            </Link>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Top header */}
          <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 lg:px-6 dark:border-gray-700 dark:bg-gray-800">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-600 hover:text-gray-900 lg:hidden dark:text-gray-300 dark:hover:text-white"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Breadcrumb / Title */}
            <div className="hidden lg:block">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentTitle}
              </h1>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <NotificationBell />

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <div className="bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 flex h-8 w-8 items-center justify-center rounded-full">
                    {user?.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:block">{user?.name}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                      <div className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user?.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                        <p className="text-primary-600 dark:text-primary-400 mt-1 text-xs">
                          {user?.role}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <LogOut className="h-4 w-4" />
                        {t("common.logout")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto p-4 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Floating "MAKEJ!" AI assistant — global, route-aware, hidden until key configured */}
      <MakejWidget />
    </AgentProvider>
  );
}
